"use client";

import React, { useState, useEffect, useRef } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStarFill } from "react-icons/pi";

interface Testimonial {
  quote: string;
  author: string;
  role: string;
  avatar: string;
  rating: number;
}

export default function OvanTestimonials({
  content,
  onContentChange,
  isSelected,
}: SectionContentProps) {
  const fontFamily = content.fontFamily || "'Inter', sans-serif";
  const [activeIndex, setActiveIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const [isDark, setIsDark] = useState(true);
  const autoPlayRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("ovan-theme") || "dark";
      setIsDark(savedTheme === "dark");
      const handleThemeChange = () => {
        setIsDark(localStorage.getItem("ovan-theme") === "dark");
      };
      window.addEventListener("ovan-theme-change", handleThemeChange);
      return () => window.removeEventListener("ovan-theme-change", handleThemeChange);
    }
  }, []);

  const defaultItems: Testimonial[] = [
    {
      quote: "My favorite part about selling my home myself was that we got to meet and get to know the people personally. This made it so much more enjoyable!",
      author: "Christa Smith",
      role: "Manager",
      avatar: "/assets/1-BcvSHoAR.jpg",
      rating: 5,
    },
    {
      quote: "Instacks made the processes so easy. Instacks instantly increased the amount of interest and ultimately saved us over $10,000.",
      author: "Calvin Carlo",
      role: "Founder",
      avatar: "/assets/2-DcEn_wk3.jpg",
      rating: 5,
    },
    {
      quote: "I have started my career as a blogger and proved myself and achieved all the milestones with great blog writing.",
      author: "Jena Barrett",
      role: "Blogger",
      avatar: "/assets/3-7jZOSvHE.jpg",
      rating: 5,
    },
    {
      quote: "Working with Instacks has been a game-changer for our team. The design quality is unmatched and delivery is always on time.",
      author: "Mike Collins",
      role: "CEO",
      avatar: "/assets/4-BAaQ2Kp5.jpg",
      rating: 5,
    },
    {
      quote: "I never thought I could build such a professional site without a team. Instacks made it all possible in just a few days.",
      author: "Sara Johnson",
      role: "Designer",
      avatar: "/assets/1-BcvSHoAR.jpg",
      rating: 5,
    },
    {
      quote: "The templates are incredibly polished. Our conversion rate went up by 40% after switching to an Instacks-powered site.",
      author: "David Park",
      role: "Marketer",
      avatar: "/assets/2-DcEn_wk3.jpg",
      rating: 5,
    },
  ];

  const items: Testimonial[] = content.items || defaultItems;

  const goTo = (idx: number) => {
    setFade(false);
    setTimeout(() => {
      setActiveIndex(idx);
      setFade(true);
    }, 250);
  };

  // Auto-play — advances every 4 seconds
  const startAutoPlay = () => {
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
    autoPlayRef.current = setInterval(() => {
      setActiveIndex(prev => {
        const next = (prev + 1) % items.length;
        setFade(false);
        setTimeout(() => setFade(true), 250);
        return next;
      });
    }, 4000);
  };

  useEffect(() => {
    startAutoPlay();
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [items.length]);

  const handleDotClick = (idx: number) => {
    goTo(idx);
    startAutoPlay();
  };

  const updateItem = (idx: number, field: keyof Testimonial, value: string | number) => {
    if (!onContentChange) return;
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], [field]: value } as Testimonial;
    onContentChange({ ...content, items: newItems });
  };

  const current = items[activeIndex] || defaultItems[0];
  const quoteColor = isDark ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.08)";

  return (
    <section
      className={`relative overflow-hidden transition-colors duration-200 ${
        isDark ? "bg-slate-900" : "bg-slate-50"
      } ${isSelected ? "ring-2 ring-blue-500 z-20 shadow-md" : ""}`}
      style={{ fontFamily }}
    >
      {/* ── Decorative open-quote: mid-left, aligned with quote text ── */}
      <span
        aria-hidden
        className="absolute top-[38%] left-[24%] select-none pointer-events-none font-serif leading-none"
        style={{ fontSize: "8.5rem", color: quoteColor, lineHeight: 1 }}
      >
        &#x201C;
      </span>

      {/* ── Decorative close-quote: mid-right, aligned with avatar ── */}
      <span
        aria-hidden
        className="absolute bottom-[22%] right-[24%] select-none pointer-events-none font-serif leading-none"
        style={{ fontSize: "8.5rem", color: quoteColor, lineHeight: 1 }}
      >
        &#x201D;
      </span>

      {/* ── Content ── */}
      <div className="relative z-10 max-w-2xl mx-auto px-6 py-20 md:py-28 text-center">

        {/* Title */}
        <h2 className={`mb-3 text-3xl md:text-4xl font-bold ${isDark ? "text-white" : "text-slate-900"}`}>
          <EditableText
            value={content.title || "What Our Client Say ?"}
            onChange={(val) => onContentChange && onContentChange({ ...content, title: val })}
            as="span"
            className={isDark ? "text-white" : "text-slate-900"}
            placeholder="Testimonials Title"
          />
        </h2>

        {/* Subtitle */}
        <p className={`text-sm md:text-base max-w-md mx-auto mb-16 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <EditableText
            value={content.text || "Our design projects are fresh and simple and will benefit your business greatly. Learn more about our work!"}
            onChange={(val) => onContentChange && onContentChange({ ...content, text: val })}
            as="span"
            placeholder="Testimonials Subtitle"
          />
        </p>

        {/* Slide — fade transition */}
        <div
          className="transition-opacity duration-300"
          style={{ opacity: fade ? 1 : 0 }}
        >
          {/* Quote text */}
          <p className={`text-xl md:text-2xl italic font-medium leading-relaxed mb-8 ${isDark ? "text-slate-200" : "text-slate-700"}`}>
            &ldquo;{" "}
            <EditableText
              value={current.quote}
              onChange={(val) => updateItem(activeIndex, "quote", val)}
              as="span"
              placeholder="Client Quote"
            />
            {" "}&rdquo;
          </p>

          {/* Stars */}
          <div className="flex justify-center gap-1 mb-6 text-yellow-400">
            {Array.from({ length: current.rating || 5 }).map((_, i) => (
              <PiStarFill key={i} className="size-5" />
            ))}
          </div>

          {/* Avatar */}
          <img
            src={current.avatar}
            alt={current.author}
            className="size-20 rounded-full object-cover mx-auto mb-3 shadow-lg ring-2 ring-slate-700"
          />

          {/* Name */}
          <p className={`text-base font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
            <EditableText
              value={current.author}
              onChange={(val) => updateItem(activeIndex, "author", val)}
              as="span"
              className={isDark ? "text-white" : "text-slate-900"}
              placeholder="Author Name"
            />
          </p>

          {/* Role */}
          <p className="text-slate-400 text-sm mt-0.5">
            <EditableText
              value={current.role}
              onChange={(val) => updateItem(activeIndex, "role", val)}
              as="span"
              placeholder="Author Role"
            />
          </p>
        </div>

        {/* Dot pagination */}
        <div className="flex justify-center gap-2 mt-10">
          {items.map((_, idx) => (
            <button
              key={idx}
              onClick={() => handleDotClick(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              className={`rounded-full transition-all duration-300 ${
                idx === activeIndex
                  ? "bg-blue-600 w-6 h-2.5"
                  : isDark
                  ? "bg-slate-600 hover:bg-slate-500 w-2.5 h-2.5"
                  : "bg-slate-300 hover:bg-slate-400 w-2.5 h-2.5"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
